'use client';

import React from 'react';
import { Download, FileText, ChevronDown } from 'lucide-react';
import type { PayrollTab, PayPeriod } from '../page';

interface PayrollHeaderProps {
  activeTab: PayrollTab;
  onTabChange: (tab: PayrollTab) => void;
  selectedPeriod: string;
  onPeriodChange: (id: string) => void;
  periods: PayPeriod[];
  currentPeriod: PayPeriod;
}

const tabs: { id: PayrollTab; label: string }[] = [
  { id: 'earnings', label: 'Earnings Summary' },
  { id: 'deductions', label: 'Deduction Tracking' },
  { id: 'comparison', label: 'Pay Period Comparison' },
];

export default function PayrollHeader({
  activeTab,
  onTabChange,
  selectedPeriod,
  onPeriodChange,
  periods,
  currentPeriod,
}: PayrollHeaderProps) {
  const handleExportCSV = () => {
    const rows = [
      ['Staff Name', 'Role', 'Department', 'Regular Hours', 'OT Hours', 'Regular Pay', 'OT Pay', 'Gross Pay', 'Deductions', 'Net Pay'],
    ];
    // Import data inline for export
    const data = [
      ['Maria Santos', 'RN', 'ICU', '72', '12', '4680', '1170', '5850', '1462', '4388'],
      ['James Okafor', 'CNA', 'Med-Surg', '80', '8', '2240', '336', '2576', '644', '1932'],
      ['Priya Nair', 'LPN', 'ER', '76', '4', '3192', '252', '3444', '861', '2583'],
      ['Derek Williams', 'RN', 'OR', '80', '16', '5440', '1632', '7072', '1768', '5304'],
      ['Aisha Johnson', 'NP', 'Urgent Care', '80', '0', '7600', '0', '7600', '1900', '5700'],
      ['Carlos Mendez', 'CNA', 'Rehab', '64', '0', '1792', '0', '1792', '448', '1344'],
      ['Linda Park', 'RN', 'Pediatrics', '72', '8', '4752', '792', '5544', '1386', '4158'],
      ['Robert Chen', 'MD', 'ER', '80', '20', '11600', '4350', '15950', '3987', '11963'],
      ['Fatima Al-Hassan', 'LPN', 'ICU', '72', '0', '3096', '0', '3096', '774', '2322'],
      ['Marcus Thompson', 'RN', 'Oncology', '80', '12', '5600', '1260', '6860', '1715', '5145'],
    ];
    const allRows = [...rows, ...data];
    const csv = allRows.map((r) => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `payroll-${currentPeriod.label.replace(/[^a-z0-9]/gi, '-')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleExportPDF = () => {
    const content = `
      MEDSTAFF PAYROLL REPORT
      Pay Period: ${currentPeriod.label}
      Generated: ${new Date().toLocaleDateString()}
      
      SUMMARY
      Total Staff: ${currentPeriod.totalStaff}
      Total Hours: ${currentPeriod.totalHours.toLocaleString()}
      Total Gross Pay: $${currentPeriod.totalGross.toLocaleString()}
      Total Deductions: $${currentPeriod.totalDeductions.toLocaleString()}
      Total Net Pay: $${currentPeriod.totalNet.toLocaleString()}
      Overtime Hours: ${currentPeriod.overtimeHours}
      Overtime Cost: $${currentPeriod.overtimeCost.toLocaleString()}
      
      EARNINGS DETAIL
      Staff Name | Role | Dept | Reg Hrs | OT Hrs | Gross Pay | Deductions | Net Pay
      Maria Santos | RN | ICU | 72 | 12 | $5,850 | $1,462 | $4,388
      James Okafor | CNA | Med-Surg | 80 | 8 | $2,576 | $644 | $1,932
      Priya Nair | LPN | ER | 76 | 4 | $3,444 | $861 | $2,583
      Derek Williams | RN | OR | 80 | 16 | $7,072 | $1,768 | $5,304
      Aisha Johnson | NP | Urgent Care | 80 | 0 | $7,600 | $1,900 | $5,700
      Carlos Mendez | CNA | Rehab | 64 | 0 | $1,792 | $448 | $1,344
      Linda Park | RN | Pediatrics | 72 | 8 | $5,544 | $1,386 | $4,158
      Robert Chen | MD | ER | 80 | 20 | $15,950 | $3,987 | $11,963
      Fatima Al-Hassan | LPN | ICU | 72 | 0 | $3,096 | $774 | $2,322
      Marcus Thompson | RN | Oncology | 80 | 12 | $6,860 | $1,715 | $5,145
    `;
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `payroll-${currentPeriod.label.replace(/[^a-z0-9]/gi, '-')}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4">
      {/* Title row */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-xl font-700 text-foreground">Payroll Reporting</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Staff earnings, deductions, and pay period analysis
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Period selector */}
          <div className="relative">
            <select
              value={selectedPeriod}
              onChange={(e) => onPeriodChange(e.target.value)}
              className="appearance-none bg-card border border-border rounded-lg pl-3 pr-8 py-2 text-sm font-500 text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 cursor-pointer"
            >
              {periods.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.label}
                </option>
              ))}
            </select>
            <ChevronDown size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
          </div>
          {/* Export buttons */}
          <button
            onClick={handleExportCSV}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-border bg-card text-sm font-500 text-foreground hover:bg-muted transition-colors"
          >
            <Download size={14} />
            Export CSV
          </button>
          <button
            onClick={handleExportPDF}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-border bg-card text-sm font-500 text-foreground hover:bg-muted transition-colors"
          >
            <FileText size={14} />
            Export PDF
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-muted p-1 rounded-lg w-fit">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => onTabChange(tab.id)}
            className={`px-4 py-1.5 rounded-md text-sm font-500 transition-all ${
              activeTab === tab.id
                ? 'bg-card text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>
    </div>
  );
}
